/**
 * FillsPopupWindow — the body rendered at /fills, designed to live in a
 * second browser window the trader opens via window.open() so they can
 * watch recent fills on a second monitor while working the dashboard.
 *
 * Standalone: renders WITHOUT the AuthShell sidebar chrome (mounted
 * outside <AuthedShell /> at the top-level router) and with a minimal
 * title bar carrying only the connection-status dot + a close button.
 *
 * Auth + data: JWT auto-inherits from the same-origin localStorage when
 * window.open() lands here, so apiFetch authenticates immediately. The
 * popup mounts its own useKalshiAccount() so the relay poller fills
 * accountStore.activityTicker for THIS window — there's no IPC sync
 * back to the parent tab. Doubles the relay's /api/trader/fills polling
 * rate (5s × 2 = ~24 req/min vs ~12 with one tab); acceptable.
 *
 * SharedWorker note: hydra's WS multiplexes across tabs (per the
 * `[Hydra] using SharedWorker` console log on g1.gbuff.com), so opening
 * a popup doesn't double the hydra upstream. The popup doesn't need
 * useHydra() for fills rendering anyway — ActivityTicker only reads
 * accountStore, no marketStore lookups.
 */

import { useEffect } from 'react';
import { X } from 'lucide-react';
import { ActivityTicker } from '../../components/ActivityTicker';
import { useAccountStore } from '../../stores/accountStore';
import { useKalshiAccount } from '../../hooks/useKalshiAccount';

export function FillsPopupWindow() {
  // Mount the relay-account poller so this window's accountStore populates
  // independently of the parent tab. Without this, status stays 'idle' and
  // ActivityTicker would render null perpetually.
  useKalshiAccount();

  const status = useAccountStore((s) => s.status);

  // OS window title — what the trader sees in the taskbar / window switcher.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Recent Fills — G1XL';
    return () => {
      document.title = prev;
    };
  }, []);

  const dotClass =
    status === 'connected'
      ? 'bg-green-500'
      : status === 'connecting'
        ? 'bg-yellow-500 animate-pulse'
        : status === 'error'
          ? 'bg-red-500'
          : 'bg-muted-foreground/40';

  const statusLabel =
    status === 'connected'
      ? 'Connected'
      : status === 'connecting'
        ? 'Connecting…'
        : status === 'error'
          ? 'Error'
          : 'Disconnected';

  return (
    <div className="bg-background text-foreground flex h-screen flex-col">
      {/* Title bar — connection state + close button. Window itself is the
          chrome (browser title bar, OS controls), so this stays compact. */}
      <div className="border-border flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} />
          <h1 className="text-foreground text-sm font-semibold">Recent Fills</h1>
          <span className="text-muted-foreground text-xs">{statusLabel}</span>
        </div>
        <button
          type="button"
          onClick={() => window.close()}
          className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1 transition-colors"
          title="Close popup"
          aria-label="Close popup"
        >
          <X size={14} />
        </button>
      </div>

      {/* Ticker — scroll if exceeds the popup's height. ActivityTicker
          renders 20 items max (ACTIVITY_TICKER_SIZE in accountStore). */}
      <div className="flex-1 overflow-y-auto p-1">
        <ActivityTicker />
      </div>
    </div>
  );
}
